import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { userApi } from '../api/axios';
import { CadreCentre, EnTeteAcces } from '../components/CadreAcces';
import { Message } from '../components/charte/Elements';
import ReglesMotDePasse from '../components/ReglesMotDePasse';
import { motDePasseConforme } from '../auth/motDePasse';

/* Mot de passe oublié, avec les routes publiques de user-service (a370081) :
   1. POST /user/forgot-password?email=… : envoie un code de 6 chiffres par
      email, valable 30 minutes (400 si l'adresse est inconnue ou inactive) ;
   2. POST /user/change-password?email=…&otp=…&password=…&salt_public= :
      201 si le mot de passe est changé, 400 si le code est faux, 410 s'il a
      expiré ou a été essayé 5 fois. salt_public ne sert qu'à l'application
      des Spotters ; le portail l'envoie vide.

   Pour ne pas révéler quelles adresses ont un compte, l'étape 1 affiche le même
   message que le compte existe ou non. */

type Etape = 'email' | 'code' | 'termine';

const MotDePasseOublie: React.FC = () => {
  const [params] = useSearchParams();
  const [etape, setEtape] = useState<Etape>('email');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => { document.title = 'Mot de passe oublié — Tribe'; }, []);

  const demanderCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErreur('');
    setInfo('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErreur('Saisissez une adresse email valide.');
      return;
    }
    setEnvoi(true);
    try {
      await userApi.post('/user/forgot-password', null, { params: { email: email.trim() } });
    } catch (err: any) {
      // 400 : adresse inconnue ou compte inactif. Même suite qu'une réussite.
      if (err?.response?.status !== 400) {
        setErreur(err?.response
          ? "Le code n'a pas pu être envoyé. Réessayez dans quelques minutes."
          : 'Impossible de joindre Tribe. Vérifiez votre connexion puis réessayez.');
        setEnvoi(false);
        return;
      }
    }
    setEnvoi(false);
    setEtape('code');
    setInfo(`Si un compte existe pour ${email.trim()}, un code vient d'y être envoyé par email. Il est valable 30 minutes ; pensez à vérifier les courriers indésirables.`);
  };

  const changerMotDePasse = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!code.trim()) { setErreur("Saisissez le code reçu par email."); return; }
    if (!motDePasseConforme(motDePasse)) { setErreur('Le nouveau mot de passe ne respecte pas les règles indiquées.'); return; }
    if (motDePasse !== confirmation) { setErreur('Les deux mots de passe ne correspondent pas.'); return; }
    setEnvoi(true);
    try {
      // Réussite : 201. Code faux : 400. Code expiré (30 minutes) ou épuisé
      // (5 essais) : 410. Le portail ne lit plus le texte de la réponse.
      await userApi.post('/user/change-password', null, {
        params: { email: email.trim(), otp: code.trim(), password: motDePasse, salt_public: '' },
      });
      setEtape('termine');
      setInfo('');
    } catch (err: any) {
      const s = err?.response?.status;
      if (s === 400) {
        setErreur('Ce code ne correspond pas. Vérifiez-le : il compte 6 chiffres. Après 5 essais, il faut en demander un nouveau.');
      } else if (s === 410) {
        setCode('');
        setErreur("Ce code n'est plus valable : il expire au bout de 30 minutes ou après 5 essais. Demandez-en un nouveau ci-dessous.");
      } else {
        setErreur(err?.response
          ? "Le mot de passe n'a pas pu être changé. Réessayez dans quelques minutes."
          : 'Impossible de joindre Tribe. Vérifiez votre connexion puis réessayez.');
      }
    } finally {
      setEnvoi(false);
    }
  };

  /* Maquette MotDePasse : un seul bloc écru, une étape à la fois. */
  return (
    <CadreCentre>
      {etape === 'termine' ? (
        <>
          <EnTeteAcces etiquette="Mot de passe" titre="Mot de passe changé" />
          <Message genre="succes">Votre mot de passe a été changé. Vous pouvez vous connecter avec le nouveau.</Message>
          <Link to="/connexion" className="bouton bouton--large">Se connecter</Link>
        </>
      ) : etape === 'email' ? (
        <>
          <EnTeteAcces etiquette="Étape 1 sur 2" titre="Mot de passe oublié">
            Indiquez votre adresse. Si un compte existe, vous recevez un code à 6 chiffres, valable 30 minutes.
          </EnTeteAcces>
          {erreur && <Message genre="erreur">{erreur}</Message>}
          <form onSubmit={demanderCode} className="pile" style={{ gap: '22px' }}>
            <div className="champ">
              <label htmlFor="oubli-email">Adresse e-mail</label>
              <input id="oubli-email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="prenom.nom@organisation.sn" required />
            </div>
            <button type="submit" className="bouton bouton--large" disabled={envoi}>
              {envoi ? 'Envoi…' : 'Recevoir le code'}
            </button>
          </form>
          <Link to="/connexion" style={{ fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>Retour à la connexion</Link>
        </>
      ) : (
        <>
          <EnTeteAcces etiquette="Étape 2 sur 2" titre="Nouveau mot de passe">
            Saisissez le code reçu et votre nouveau mot de passe. Vous avez 5 essais.
          </EnTeteAcces>
          {info && <Message genre="info">{info}</Message>}
          <form onSubmit={changerMotDePasse} className="pile" style={{ gap: '22px' }}>
            <div className="pile" style={{ gap: '14px' }}>
              <div className="champ">
                <label htmlFor="oubli-code">Code reçu</label>
                <input id="oubli-code" className="saisie-code" inputMode="numeric" autoComplete="one-time-code" value={code}
                  onChange={e => setCode(e.target.value)} placeholder="000 000" required />
              </div>
              {erreur && <Message genre="erreur">{erreur}</Message>}
              <div className="champ">
                <label htmlFor="oubli-mdp">Nouveau mot de passe</label>
                <input id="oubli-mdp" type="password" autoComplete="new-password" value={motDePasse}
                  onChange={e => setMotDePasse(e.target.value)} aria-describedby="oubli-mdp-regles" placeholder="8 caractères minimum" required />
                <ReglesMotDePasse id="oubli-mdp-regles" motDePasse={motDePasse} />
              </div>
              <div className="champ">
                <label htmlFor="oubli-confirmation">Confirmer le nouveau mot de passe</label>
                <input id="oubli-confirmation" type="password" autoComplete="new-password" value={confirmation}
                  onChange={e => setConfirmation(e.target.value)} required />
              </div>
            </div>
            <button type="submit" className="bouton bouton--large" disabled={envoi}>
              {envoi ? 'Enregistrement…' : 'Changer le mot de passe'}
            </button>
          </form>
          <p style={{ fontSize: '13px', color: 'var(--gris)', textAlign: 'center' }}>
            Code expiré ?{' '}
            <button type="button" className="lien-retour--bouton" style={{ fontSize: '13px' }} onClick={() => demanderCode()} disabled={envoi}>
              Recevoir un nouveau code
            </button>
          </p>
          <Link to="/connexion" style={{ fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>Retour à la connexion</Link>
        </>
      )}
    </CadreCentre>
  );
};

export default MotDePasseOublie;
